import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box } from "@mui/material";
import qs from "query-string";
import { useEffect, useMemo, useRef, useState } from "react";

import { useDebouncedEffect } from "../../../../../../../hooks/useDebounceEffect";
import useService from "../../../../../../../hooks/useService";
import AutoCompleteDropdown from "../../../../../../ui library/components/dropdowns/AutoCompleteDropdown";
import QuestionTitle from "../../../components/QuestionTitle";
import sharedStyles from "../styles/formStyles";

const extractPlaceholders = (url = "") => {
  const placeholders = new Set();
  const rawMatches = url.matchAll(/\{([^}]+)\}/g);
  for (const [, match] of rawMatches) {
    placeholders.add(match);
  }

  const encodedMatches = url.matchAll(/%7B([^%]+?)%7D/gi);
  for (const [, match] of encodedMatches) {
    placeholders.add(match);
  }

  return Array.from(placeholders);
};

export function WithSearchSelectAnswer({
  typeProperties,
  enumUrl = "",
  label,
  name,
  onChange,
  value = "",
  placeholder,
  error = false,
  formValues = {},
  disabled = false,
}) {
  const [val, setVal] = useState(value);
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState("");
  const enumRepository = useService("repositories/enum-repository");

  const placeholders = useMemo(() => extractPlaceholders(enumUrl), [enumUrl]);

  const resolvedDependencies = useMemo(() => {
    if (!placeholders.length) {
      return {};
    }

    return placeholders.reduce((acc, field) => {
      acc[field] = formValues?.[field];
      return acc;
    }, {});
  }, [placeholders, formValues]);

  const serializedDependencies = JSON.stringify(resolvedDependencies);
  const prevDependenciesRef = useRef(serializedDependencies);

  useEffect(() => {
    setVal(value ?? "");
  }, [value]);

  useEffect(() => {
    if (!placeholders.length) {
      return;
    }

    if (prevDependenciesRef.current !== serializedDependencies) {
      prevDependenciesRef.current = serializedDependencies;
      setOptions([]);
      setVal("");
      setSearch("");
      onChange(name, "");
    }
  }, [name, onChange, placeholders.length, serializedDependencies]);

  useDebouncedEffect(
    async () => {
      if (!enumUrl) {
        return;
      }

      if (
        placeholders.length &&
        placeholders.some((field) => {
          const dependencyValue = resolvedDependencies[field];
          return (
            dependencyValue === undefined || dependencyValue === null || dependencyValue === ""
          );
        })
      ) {
        return;
      }

      try {
        let resolvedUrl = enumUrl.replace("/api/", "");

        placeholders.forEach((field) => {
          const dependencyValue = resolvedDependencies[field] ?? "";
          const encodedPlaceholder = new RegExp(`%7B${field}%7D`, "gi");
          const rawPlaceholder = new RegExp(`{${field}}`, "g");
          resolvedUrl = resolvedUrl
            .replace(encodedPlaceholder, encodeURIComponent(dependencyValue))
            .replace(rawPlaceholder, dependencyValue);
        });

        const [baseUrl, existingQuery] = resolvedUrl.split("?");
        const existingParams = existingQuery ? qs.parse(existingQuery) : {};
        const queryParams = {
          ...existingParams,
          ...(search ? { search } : {}),
          ...(typeProperties?.resLimit ? { limit: typeProperties.resLimit } : {}),
        };
        const paramString = qs.stringify(queryParams);
        const urlPath = baseUrl + (paramString ? `?${paramString}` : "");

        const resp = await enumRepository.query({ urlPath });
        const list = Array.isArray(resp?.results) ? resp.results : resp;
        setOptions(
          (list || []).map(({ key, value, label }) => ({
            key,
            value: value || label,
          }))
        );
      } catch (error) {
        console.error("Something went wrong fetching options", error);
        setOptions([]);
      }
    },
    [enumUrl, typeProperties, search, enumRepository, placeholders, serializedDependencies],
    250
  );

  const handleValueChange = (event, selectedValue) => {
    const nextValue = selectedValue?.key ?? "";
    setVal(nextValue);
    onChange(name, nextValue);
  };

  const handleInputChange = (event, inputValue, reason) => {
    if (reason === "input" && inputValue !== search) {
      setSearch(inputValue);
    } else if (reason === "clear") {
      setSearch("");
      setVal("");
      onChange(name, "");
    }
  };

  useEffect(() => {
    setSearch(val || "");
  }, [val]);

  const selectedOption = val ? options.find((opt) => opt.key === val) : null;

  return (
    <Box mb={2}>
      <QuestionTitle label={label} sx={sharedStyles.label(false)} />
      <AutoCompleteDropdown
        data={options}
        initValue={selectedOption}
        getOptionLabel={(option) => option?.value || option || ""}
        isOptionEqualToValue={(option, optionValue) =>
          option?.key === optionValue?.key || option === optionValue
        }
        onValueChange={handleValueChange}
        onInputChange={handleInputChange}
        textFieldSx={sharedStyles.textField}
        placeholder={placeholder}
        disabled={disabled}
        selectProps={{
          IconComponent: KeyboardArrowDownIcon,
          MenuProps: { PaperProps: { sx: sharedStyles.selectMenuPaper } },
        }}
        error={error}
      />
    </Box>
  );
}
